import { useLoaderData } from "react-router";
import { DataSourceBadge, DataSourceNotice } from "@/components/layout/data-source-notice";
import { PageHeader } from "@/components/layout/page-header";
import { Badge, EmptyState, Panel, type Tone } from "@/components/ui";
import { useDocumentTitle } from "@/lib/document-title";
import { formatDateTime } from "@/lib/format";
import type { Integration } from "@/lib/types";
import type { ApiFailure } from "@/services/api-client";
import { listIntegrations } from "@/services/reports";

const STATUS_TONE: Record<Integration["status"], Tone> = {
  connected: "positive",
  error: "negative",
  available: "neutral",
};

interface IntegrationsData {
  integrations: Integration[];
  fallback: ApiFailure | null;
}

export async function integrationsLoader(): Promise<IntegrationsData> {
  const { data, fallback } = await listIntegrations();
  return { integrations: data, fallback };
}

export function IntegrationsPage() {
  useDocumentTitle("Integrations");
  const { integrations, fallback } = useLoaderData<IntegrationsData>();
  const broken = integrations.filter((entry) => entry.status === "error");

  return (
    <>
      <PageHeader
        title="Integrations"
        description="Where evidence comes from. A broken connection becomes a missing document on a case."
        action={<DataSourceBadge fallback={fallback} />}
      />

      <div className="mx-auto max-w-5xl space-y-4 p-6">
        <DataSourceNotice fallback={fallback} />

        {broken.length > 0 ? (
          <div className="rounded-lg border border-negative/20 bg-negative-soft px-4 py-3 text-xs text-negative">
            {broken.length} connection{broken.length === 1 ? " is" : "s are"} failing:{" "}
            {broken.map((entry) => entry.name).join(", ")}. Evidence from{" "}
            {broken.length === 1 ? "it" : "them"} will not refresh until reconnected.
          </div>
        ) : null}

        <Panel>
          {integrations.length === 0 ? (
            <EmptyState
              title="No connections"
              detail="Connect a banking, accounting or bureau provider to start pulling evidence."
            />
          ) : (
            <ul className="divide-y divide-line">
              {integrations.map((integration) => (
                <li
                  key={integration.id}
                  className="flex flex-wrap items-start justify-between gap-4 px-4 py-3"
                >
                  <div className="min-w-0 max-w-xl">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-ink">{integration.name}</span>
                      <Badge>{integration.category}</Badge>
                    </div>
                    <p className="mt-0.5 text-xs leading-relaxed text-ink-muted">
                      {integration.detail}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <Badge tone={STATUS_TONE[integration.status]}>{integration.status}</Badge>
                    {integration.lastSync ? (
                      <span className="numeric text-[0.6875rem] text-ink-faint">
                        Synced {formatDateTime(integration.lastSync)}
                      </span>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </>
  );
}
